import { useState, useEffect } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { copyWorkingHours } from "@/lib/workingHoursCopy";
import { Plus, Pencil, Loader2, User, Clock, Users, MapPin, Trash2, Camera, UserCircle2, GitBranch, Scissors as ScissorsIcon } from "lucide-react";
import { useRef } from "react";

type ProfForm = {
  name: string;
  bio: string;
  phone: string;
  email: string;
  color: string;
  allowOverlap: boolean;
  stationName: string;
  isActive: boolean;
  branchId: number | null;
  executesServices: boolean;
  isOnlineMeeting: boolean;
};
const emptyForm: ProfForm = { name: "", bio: "", phone: "", email: "", color: "#8B6914", allowOverlap: false, stationName: "", isActive: true, branchId: null, executesServices: true, isOnlineMeeting: false };

const DAYS = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

export default function AdminProfessionals() {
  const utils = trpc.useUtils();
  const { data: professionals, isLoading } = trpc.professionals.listAdmin.useQuery();
  // Busca filiais do salão para o seletor
  const { data: salonInfo } = trpc.salon.getMySalon.useQuery(undefined, { retry: false });
  const salonId = salonInfo?.id ?? 0;
  const { data: branches } = trpc.branches.list.useQuery(
    undefined,
    { enabled: salonId > 0 }
  );
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [form, setForm] = useState<ProfForm>(emptyForm);
  const [hoursDialogOpen, setHoursDialogOpen] = useState(false);
  const [selectedProfId, setSelectedProfId] = useState<number | null>(null);
  const [workHours, setWorkHours] = useState<{ dayOfWeek: number; startTime: string; endTime: string }[]>(
    Array.from({ length: 7 }, (_, i) => ({ dayOfWeek: i, startTime: "09:00", endTime: "18:00" }))
  );
  const [enabledDays, setEnabledDays] = useState<boolean[]>(Array(7).fill(false).map((_, i) => i >= 1 && i <= 5));
  const [copySourceDay, setCopySourceDay] = useState(1);
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [copySourceId, setCopySourceId] = useState<string>("");
  const [copyTargetId, setCopyTargetId] = useState<string>("");
  const [servicesDialogOpen, setServicesDialogOpen] = useState(false);
  const [servicesProfessionalId, setServicesProfessionalId] = useState<number | null>(null);
  const [selectedServiceIds, setSelectedServiceIds] = useState<number[]>([]);

  const { data: adminServices } = trpc.services.listAdmin.useQuery();
  const { data: serviceProfessionalsMap } = trpc.services.getAllProfessionalsMap.useQuery();

  // Foto do profissional
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [photoBase64, setPhotoBase64] = useState<string | null>(null);
  const [photoMime, setPhotoMime] = useState<"image/jpeg" | "image/png" | "image/webp">("image/jpeg");
  const photoInputRef = useRef<HTMLInputElement>(null);

  const uploadAvatarMutation = (trpc as any).professionals.uploadAvatar.useMutation({
    onSuccess: (data: any) => {
      toast.success("Foto atualizada!");
      utils.professionals.listAdmin.invalidate();
      setPhotoPreview(data.avatarUrl);
      setPhotoBase64(null);
    },
    onError: (err: any) => toast.error("Erro ao enviar foto", { description: err.message }),
  });

  // Busca os horários do profissional selecionado
  const { data: existingHours, isLoading: loadingHours } = trpc.professionals.getWorkingHours.useQuery(
    { professionalId: selectedProfId ?? 0 },
    { enabled: !!selectedProfId && hoursDialogOpen }
  );

  // ✅ CORREÇÃO DO BUG: inicializa workHours e enabledDays QUANDO existingHours carregar
  useEffect(() => {
    if (!hoursDialogOpen || !existingHours) return;
    const newWorkHours = Array.from({ length: 7 }, (_, i) => {
      const existing = existingHours.find((h) => h.dayOfWeek === i);
      return {
        dayOfWeek: i,
        startTime: existing?.startTime ?? "09:00",
        endTime: existing?.endTime ?? "18:00",
      };
    });
    const newEnabledDays = Array.from({ length: 7 }, (_, i) =>
      existingHours.some((h) => h.dayOfWeek === i)
    );
    setWorkHours(newWorkHours);
    setEnabledDays(newEnabledDays);
  }, [existingHours, hoursDialogOpen]);

  const createMutation = trpc.professionals.create.useMutation({
    onSuccess: () => {
      toast.success("Profissional criado!");
      utils.professionals.listAdmin.invalidate();
      setDialogOpen(false);
    },
    onError: (e) => toast.error(e.message),
  });

  const updateMutation = trpc.professionals.update.useMutation({
    onSuccess: () => {
      toast.success("Profissional atualizado!");
      utils.professionals.listAdmin.invalidate();
      setDialogOpen(false);
    },
    onError: (e) => toast.error(e.message),
  });

  const deleteMutation = trpc.professionals.delete.useMutation({
    onSuccess: () => {
      toast.success("Profissional retirado da lista. Histórico preservado.");
      utils.professionals.listAdmin.invalidate();
      utils.professionals.listForSchedule.invalidate();
      utils.professionals.list.invalidate();
      utils.services.getAllProfessionalsMap.invalidate();
      setDialogOpen(false);
    },
    onError: (e) => toast.error(e.message),
  });

  const setHoursMutation = trpc.professionals.setWorkingHours.useMutation({
    onSuccess: () => {
      toast.success("Horários salvos com sucesso!");
      setHoursDialogOpen(false);
    },
    onError: (e) => toast.error(e.message),
  });

  const copyProfileMutation = trpc.professionals.copyProfile.useMutation({
    onSuccess: (result) => {
      toast.success("Perfil copiado com sucesso!", {
        description: `${result.workingHoursCount} horários e ${result.serviceCount} serviços atribuídos ao profissional de destino.`,
      });
      setCopyDialogOpen(false);
      setCopySourceId("");
      setCopyTargetId("");
    },
    onError: (e) => toast.error("Não foi possível copiar o perfil", { description: e.message }),
  });

  const setServicesMutation = trpc.professionals.setServices.useMutation({
    onSuccess: () => {
      toast.success("Serviços do profissional atualizados!");
      utils.services.getAllProfessionalsMap.invalidate();
      setServicesDialogOpen(false);
    },
    onError: (e) => toast.error("Não foi possível atualizar os serviços", { description: e.message }),
  });

  const openCreate = () => {
    setEditId(null);
    setForm(emptyForm);
    setPhotoPreview(null);
    setPhotoBase64(null);
    setDialogOpen(true);
  };

  const openEdit = (p: NonNullable<typeof professionals>[number]) => {
    setEditId(p.id);
    setForm({
      name: p.name,
      bio: p.bio ?? "",
      phone: p.phone ?? "",
      email: p.email ?? "",
      color: p.color ?? "#8B6914",
      allowOverlap: (p as any).allowOverlap ?? false,
      stationName: (p as any).stationName ?? "",
      isActive: (p as any).isActive ?? true,
      branchId: (p as any).branchId ?? null,
      executesServices: (p as any).executesServices ?? true,
      isOnlineMeeting: (p as any).isOnlineMeeting ?? false,
    });
    setPhotoPreview((p as any).avatarUrl ?? null);
    setPhotoBase64(null);
    setDialogOpen(true);
  };

  const openHours = (profId: number) => {
    // Resetar para defaults antes de abrir — o useEffect vai preencher quando existingHours carregar
    setWorkHours(Array.from({ length: 7 }, (_, i) => ({ dayOfWeek: i, startTime: "09:00", endTime: "18:00" })));
    setEnabledDays(Array(7).fill(false).map((_, i) => i >= 1 && i <= 5));
    setSelectedProfId(profId);
    setHoursDialogOpen(true);
  };

  const handleSave = () => {
    if (!form.name.trim()) return toast.error("Nome é obrigatório");
    if (editId) {
      updateMutation.mutate({ id: editId, ...form });
      if (photoBase64) {
        uploadAvatarMutation.mutate({ professionalId: editId, base64: photoBase64, mimeType: photoMime });
      }
    } else {
      createMutation.mutate(form, {
        onSuccess: () => {
          // Após criar, busca o ID do profissional recém-criado para upload da foto
          // O invalidate vai recarregar a lista e a foto será carregada na próxima edição
        },
      });
    }
  };

  const handleSaveHours = () => {
    if (!selectedProfId) return;
    const hours = workHours.filter((_, i) => enabledDays[i]);
    setHoursMutation.mutate({ professionalId: selectedProfId, hours });
  };

  const handleCopyHours = (scope: "enabled" | "all") => {
    const next = copyWorkingHours(workHours, enabledDays, copySourceDay, scope);
    setWorkHours(next.workHours);
    setEnabledDays(next.enabledDays);
    toast.success(scope === "all" ? "Horário aplicado a todos os dias." : "Horário aplicado aos dias marcados.");
  };

  const openCopyProfile = (targetId?: number) => {
    setCopySourceId("");
    setCopyTargetId(targetId ? String(targetId) : "");
    setCopyDialogOpen(true);
  };

  const openServices = (professionalId: number) => {
    const assigned = Object.entries(serviceProfessionalsMap ?? {})
      .filter(([, professionalIds]) => professionalIds.includes(professionalId))
      .map(([serviceId]) => Number(serviceId));
    setServicesProfessionalId(professionalId);
    setSelectedServiceIds(assigned);
    setServicesDialogOpen(true);
  };

  const toggleService = (serviceId: number) => {
    setSelectedServiceIds((current) => current.includes(serviceId)
      ? current.filter((id) => id !== serviceId)
      : [...current, serviceId]);
  };

  const handleSaveServices = () => {
    if (!servicesProfessionalId) return;
    setServicesMutation.mutate({
      professionalId: servicesProfessionalId,
      serviceIds: selectedServiceIds,
    });
  };

  const handleCopyProfile = () => {
    const sourceId = Number(copySourceId);
    const targetId = Number(copyTargetId);
    if (!sourceId || !targetId) return toast.error("Selecione a origem e o destino");
    if (sourceId === targetId) return toast.error("Escolha profissionais diferentes");
    const source = professionals?.find((p) => p.id === sourceId);
    const target = professionals?.find((p) => p.id === targetId);
    if (!source || !target) return toast.error("Profissional não encontrado");
    if (!confirm(`Copiar horários e serviços de ${source.name} para ${target.name}? Os horários e serviços atuais do destino serão substituídos.`)) return;
    copyProfileMutation.mutate({ sourceProfessionalId: sourceId, targetProfessionalId: targetId });
  };

  function handlePhotoChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Foto muito grande. Máximo 5MB."); return; }
    const mime = file.type as "image/jpeg" | "image/png" | "image/webp";
    setPhotoMime(mime);
    const reader = new FileReader();
    reader.onload = (ev) => {
      const b64 = ev.target?.result as string;
      setPhotoBase64(b64);
      setPhotoPreview(b64);
    };
    reader.readAsDataURL(file);
  }

  const isPending = createMutation.isPending || updateMutation.isPending || deleteMutation.isPending || uploadAvatarMutation.isPending;

  return (
    <div className="space-y-4 md:space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl md:text-2xl font-[Cormorant_Garamond,serif] font-semibold">Profissionais</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Gerencie a equipe do salão</p>
        </div>
        <Button onClick={openCreate} className="bg-primary text-primary-foreground shrink-0">
          <Plus className="w-4 h-4 mr-1" /> Novo Profissional
        </Button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {professionals?.map((prof) => (
            <Card key={prof.id} className="border-border/50 hover:border-primary/30 transition-all">
              <CardContent className="p-5">
                <div className="flex items-start gap-3">
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg shrink-0"
                    style={{ backgroundColor: prof.color ?? "#8B6914" }}
                  >
                    {prof.avatarUrl ? (
                      <img src={prof.avatarUrl} alt={prof.name} className="w-full h-full rounded-full object-cover" />
                    ) : (
                      prof.name.charAt(0).toUpperCase()
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold text-foreground truncate">{prof.name}</h3>
                      {!prof.isActive && <Badge variant="secondary" className="text-xs">Inativo</Badge>}
                      {!(prof as any).executesServices && (
                        <Badge variant="outline" className="text-xs gap-1 text-amber-600 border-amber-300 bg-amber-50 dark:bg-amber-950/30">
                          <ScissorsIcon className="w-3 h-3" /> Administrativo
                        </Badge>
                      )}
                      {prof.allowOverlap && (
                        <Badge variant="outline" className="text-xs gap-1 text-blue-600 border-blue-300">
                          <Users className="w-3 h-3" /> Multi-cliente
                        </Badge>
                      )}
                    </div>
                    {prof.bio && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{prof.bio}</p>}
                    {prof.email && <p className="text-xs text-muted-foreground mt-0.5">{prof.email}</p>}
                    {(prof as any).stationName && (
                      <div className="flex items-center gap-1 mt-1.5">
                        <MapPin className="w-3 h-3 text-primary/70 shrink-0" />
                        <span className="text-xs font-medium text-primary/80 truncate">{(prof as any).stationName}</span>
                      </div>
                    )}
                    {(prof as any).branchId && branches && (
                      <div className="flex items-center gap-1 mt-1">
                        <GitBranch className="w-3 h-3 text-amber-600/70 shrink-0" />
                        <span className="text-xs text-amber-700/80 truncate">
                          {branches.find((b: any) => b.id === (prof as any).branchId)?.name ?? "Filial"}
                        </span>
                      </div>
                    )}
                    {!(prof as any).branchId && (
                      <div className="flex items-center gap-1 mt-1">
                        <GitBranch className="w-3 h-3 text-muted-foreground/50 shrink-0" />
                        <span className="text-xs text-muted-foreground/60">Todas as unidades</span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 mt-4">
                  <Button variant="outline" size="sm" className="flex-1 text-xs" onClick={() => openEdit(prof)}>
                    <Pencil className="w-3 h-3 mr-1" /> Editar
                  </Button>
                  <Button variant="outline" size="sm" className="flex-1 text-xs gap-1" onClick={() => openHours(prof.id)}>
                    <Clock className="w-3 h-3" /> Horários
                  </Button>
                  <Button variant="outline" size="sm" className="flex-1 text-xs gap-1" onClick={() => openCopyProfile(prof.id)}>
                    <Users className="w-3 h-3" /> Copiar perfil
                  </Button>
                  {(prof as any).executesServices && (
                    <Button variant="outline" size="sm" className="flex-1 text-xs gap-1" onClick={() => openServices(prof.id)}>
                      <ScissorsIcon className="w-3 h-3" /> Serviços
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
          {(!professionals || professionals.length === 0) && (
            <div className="col-span-2 text-center py-12 text-muted-foreground">
              <User className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="mb-3">Nenhum profissional cadastrado.</p>
              <Button onClick={openCreate} variant="outline">Adicionar primeiro profissional</Button>
            </div>
          )}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-[Cormorant_Garamond,serif]">
              {editId ? "Editar Profissional" : "Novo Profissional"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {/* Foto do profissional */}
            <div className="flex flex-col items-center gap-2">
              <div className="relative group/photo cursor-pointer" onClick={() => photoInputRef.current?.click()}>
                {photoPreview ? (
                  <img src={photoPreview} alt="Foto" className="w-20 h-20 rounded-full object-cover border-2 border-border" />
                ) : (
                  <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center border-2 border-dashed border-border">
                    <UserCircle2 className="w-10 h-10 text-muted-foreground" />
                  </div>
                )}
                <div className="absolute inset-0 rounded-full bg-black/50 opacity-0 group-hover/photo:opacity-100 flex items-center justify-center transition-opacity">
                  <Camera className="w-5 h-5 text-white" />
                </div>
              </div>
              <span className="text-xs text-muted-foreground">Clique para adicionar foto (máx. 5MB)</span>
              <input ref={photoInputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handlePhotoChange} />
            </div>

            <div>
              <Label className="text-sm">Nome *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Nome completo"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-sm">Bio</Label>
              <Textarea
                value={form.bio}
                onChange={(e) => setForm({ ...form, bio: e.target.value })}
                placeholder="Especialidades e experiência..."
                className="mt-1 resize-none"
                rows={2}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm">Telefone</Label>
                <Input
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="(11) 99999-9999"
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-sm">Email</Label>
                <Input
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="email@exemplo.com"
                  className="mt-1"
                />
              </div>
            </div>
            <div>
              <Label className="text-sm">Cor no calendário</Label>
              <div className="flex items-center gap-2 mt-1">
                <input
                  type="color"
                  value={form.color}
                  onChange={(e) => setForm({ ...form, color: e.target.value })}
                  className="w-10 h-10 rounded cursor-pointer border border-border"
                />
                <span className="text-sm text-muted-foreground">{form.color}</span>
              </div>
            </div>
            <div>
              <Label className="text-sm flex items-center gap-1.5">
                <span>Sala / Estação</span>
                <span className="text-xs text-muted-foreground font-normal">(exibido no Painel de Chamada)</span>
              </Label>
              <Input
                value={form.stationName}
                onChange={(e) => setForm({ ...form, stationName: e.target.value })}
                placeholder="Ex: Sala 1, Cadeira 3, Box A"
                className="mt-1"
                maxLength={64}
              />
            </div>
            {/* Seletor de Filial */}
            {branches && branches.length > 0 && (
              <div>
                <Label className="text-sm flex items-center gap-1.5">
                  <GitBranch className="w-3.5 h-3.5 text-amber-600" />
                  <span>Filial</span>
                  <span className="text-xs text-muted-foreground font-normal">(null = atende todas as unidades)</span>
                </Label>
                <Select
                  value={form.branchId ? String(form.branchId) : "all"}
                  onValueChange={(v) => setForm({ ...form, branchId: v === "all" ? null : Number(v) })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Selecionar filial..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas as unidades (sede)</SelectItem>
                    {branches.map((b: any) => (
                      <SelectItem key={b.id} value={String(b.id)}>{b.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {/* Toggle allowOverlap */}
            <div className="flex items-center justify-between rounded-lg border border-border p-3 bg-muted/30">
              <div className="space-y-0.5">
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-blue-500" />
                  <Label className="text-sm font-medium">Múltiplos clientes no mesmo horário</Label>
                </div>
                <p className="text-xs text-muted-foreground">
                  Permite agendar mais de um cliente no mesmo slot de horário para este profissional.
                </p>
              </div>
              <Switch
                checked={form.allowOverlap}
                onCheckedChange={(v) => setForm({ ...form, allowOverlap: v })}
              />
            </div>
            {/* Toggle executesServices */}
            <div className="flex items-center justify-between rounded-lg border border-amber-200 dark:border-amber-800 p-3 bg-amber-50 dark:bg-amber-950/30">
              <div className="space-y-0.5">
                <div className="flex items-center gap-2">
                  <ScissorsIcon className="w-4 h-4 text-amber-600" />
                  <Label className="text-sm font-medium">Executa Serviços</Label>
                </div>
                <p className="text-xs text-muted-foreground">
                  Desative para funcionários administrativos ou com salário fixo que não aparecem no agendamento.
                </p>
              </div>
              <Switch
                checked={form.executesServices}
                onCheckedChange={(v) => setForm({ ...form, executesServices: v })}
              />
            </div>
            <div className="flex items-center justify-between rounded-lg border border-sky-200 dark:border-sky-800 p-3 bg-sky-50 dark:bg-sky-950/30">
              <div className="space-y-0.5 pr-3">
                <Label className="text-sm font-medium">Reunião online com link</Label>
                <p className="text-xs text-muted-foreground">
                  Nas confirmações deste profissional, informa que a reunião será online e que o link será enviado 20 minutos antes.
                </p>
              </div>
              <Switch
                checked={form.isOnlineMeeting}
                onCheckedChange={(v) => setForm({ ...form, isOnlineMeeting: v })}
              />
            </div>
            {/* Toggle isActive */}
            {editId && (
              <div className="flex items-center justify-between rounded-lg border border-border p-3 bg-muted/30">
                <div className="space-y-0.5">
                  <Label className="text-sm font-medium">Profissional Ativo</Label>
                  <p className="text-xs text-muted-foreground">
                    Desative para ocultar este profissional da agenda e do atendimento da IA.
                  </p>
                </div>
                <Switch
                  checked={form.isActive}
                  onCheckedChange={(v) => setForm({ ...form, isActive: v })}
                />
              </div>
            )}
          </div>
          <DialogFooter className="flex items-center justify-between">
            <div>
              {editId && (
                <Button
                  variant="destructive"
                  onClick={() => {
                    if (confirm("Retirar este profissional da lista operacional e remover suas regras e vínculos de serviços? Os atendimentos, pagamentos e comissões já registrados serão preservados.")) {
                      deleteMutation.mutate({ id: editId });
                    }
                  }}
                  disabled={isPending}
                  className="mr-2"
                  size="sm"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Excluir
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button onClick={handleSave} disabled={isPending} className="bg-primary text-primary-foreground">
                {isPending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                {editId ? "Salvar" : "Criar"}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Services performed by professional */}
      <Dialog open={servicesDialogOpen} onOpenChange={setServicesDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90dvh] flex flex-col">
          <DialogHeader className="shrink-0">
            <DialogTitle className="font-[Cormorant_Garamond,serif]">Serviços que o profissional executa</DialogTitle>
          </DialogHeader>
          {(() => {
            const professional = professionals?.find((item) => item.id === servicesProfessionalId);
            return (
              <>
                <div className="rounded-lg border border-primary/20 bg-primary/5 px-3 py-2 shrink-0">
                  <p className="text-sm font-semibold">{professional?.name ?? "Profissional"}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Selecione somente os serviços que esta pessoa realmente executa. A agenda e a IA usarão esta lista.
                  </p>
                </div>
                <div className="flex items-center justify-between gap-2 py-1 shrink-0">
                  <span className="text-xs text-muted-foreground">{selectedServiceIds.length} serviço{selectedServiceIds.length !== 1 ? "s" : ""} selecionado{selectedServiceIds.length !== 1 ? "s" : ""}</span>
                  <div className="flex gap-1">
                    <Button type="button" size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setSelectedServiceIds((adminServices ?? []).map((service) => service.id))}>Todos</Button>
                    <Button type="button" size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setSelectedServiceIds([])}>Limpar</Button>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 overflow-y-auto pr-1 flex-1">
                  {(adminServices ?? []).map((service) => {
                    const selected = selectedServiceIds.includes(service.id);
                    return (
                      <label
                        key={service.id}
                        className={`flex items-center gap-2.5 rounded-lg border px-3 py-2.5 cursor-pointer transition-colors ${selected ? "border-primary/60 bg-primary/5" : "border-border/50 hover:border-border"}`}
                      >
                        <Checkbox checked={selected} onCheckedChange={() => toggleService(service.id)} className="shrink-0" />
                        <span className="min-w-0 flex-1">
                          <span className="block text-sm font-medium truncate">{service.name}</span>
                          <span className="block text-xs text-muted-foreground truncate">{service.category || "Sem categoria"}</span>
                        </span>
                      </label>
                    );
                  })}
                  {adminServices?.length === 0 && <p className="col-span-2 text-sm text-muted-foreground text-center py-8">Nenhum serviço cadastrado.</p>}
                </div>
                <DialogFooter className="shrink-0">
                  <Button variant="outline" onClick={() => setServicesDialogOpen(false)}>Cancelar</Button>
                  <Button onClick={handleSaveServices} disabled={setServicesMutation.isPending} className="bg-primary text-primary-foreground">
                    {setServicesMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                    Salvar serviços
                  </Button>
                </DialogFooter>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Copy Professional Profile Dialog */}
      <Dialog open={copyDialogOpen} onOpenChange={setCopyDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-[Cormorant_Garamond,serif]">Copiar perfil operacional</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">
              Copie os horários e os serviços atendidos de um profissional para outro do mesmo salão. Dados pessoais, agendamentos, comissões e permissões não serão alterados.
            </p>
            <div className="space-y-2">
              <Label>Copiar de (origem) *</Label>
              <Select value={copySourceId} onValueChange={setCopySourceId}>
                <SelectTrigger><SelectValue placeholder="Selecione o profissional de origem" /></SelectTrigger>
                <SelectContent>
                  {professionals?.map((professional) => (
                    <SelectItem key={professional.id} value={String(professional.id)}>{professional.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Copiar para (destino) *</Label>
              <Select value={copyTargetId} onValueChange={setCopyTargetId}>
                <SelectTrigger><SelectValue placeholder="Selecione o profissional de destino" /></SelectTrigger>
                <SelectContent>
                  {professionals?.map((professional) => (
                    <SelectItem key={professional.id} value={String(professional.id)}>{professional.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-100">
              A operação substitui os horários e os serviços atualmente atribuídos ao destino. Ela será registrada na auditoria do salão.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCopyDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleCopyProfile} disabled={copyProfileMutation.isPending} className="bg-primary text-primary-foreground">
              {copyProfileMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              Copiar perfil
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Working Hours Dialog */}
      <Dialog open={hoursDialogOpen} onOpenChange={setHoursDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-[Cormorant_Garamond,serif]">Horários de Trabalho</DialogTitle>
          </DialogHeader>
          {loadingHours ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
              <span className="ml-2 text-sm text-muted-foreground">Carregando horários...</span>
            </div>
          ) : (
            <div className="space-y-3 py-2 max-h-[400px] overflow-y-auto">
              <div className="rounded-lg border border-primary/20 bg-primary/5 p-3 space-y-2">
                <p className="text-xs font-semibold text-foreground">Copiar jornada fixa</p>
                <div className="flex flex-col gap-2 sm:flex-row">
                  <Select value={String(copySourceDay)} onValueChange={(value) => setCopySourceDay(Number(value))}>
                    <SelectTrigger className="h-8 flex-1 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>{DAYS.map((day, index) => <SelectItem key={day} value={String(index)}>{day} como origem</SelectItem>)}</SelectContent>
                  </Select>
                  <Button type="button" size="sm" variant="outline" onClick={() => handleCopyHours("enabled")}>Dias marcados</Button>
                  <Button type="button" size="sm" onClick={() => handleCopyHours("all")}>Todos os dias</Button>
                </div>
                <p className="text-[11px] text-muted-foreground">Copia início, fim e também a folga do dia escolhido. Depois, você pode ajustar qualquer dia individualmente.</p>
              </div>
              {DAYS.map((day, i) => (
                <div key={i} className="flex items-center gap-3">
                  <label className="flex items-center gap-2 w-16 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={enabledDays[i]}
                      onChange={(e) => {
                        const next = [...enabledDays];
                        next[i] = e.target.checked;
                        setEnabledDays(next);
                      }}
                      className="rounded"
                    />
                    <span className="text-sm font-medium">{day}</span>
                  </label>
                  {enabledDays[i] && (
                    <div className="flex items-center gap-2 flex-1">
                      <Input
                        type="time"
                        value={workHours[i]?.startTime ?? "09:00"}
                        onChange={(e) => {
                          const next = [...workHours];
                          next[i] = { ...next[i]!, startTime: e.target.value };
                          setWorkHours(next);
                        }}
                        className="text-xs h-8"
                      />
                      <span className="text-xs text-muted-foreground">até</span>
                      <Input
                        type="time"
                        value={workHours[i]?.endTime ?? "18:00"}
                        onChange={(e) => {
                          const next = [...workHours];
                          next[i] = { ...next[i]!, endTime: e.target.value };
                          setWorkHours(next);
                        }}
                        className="text-xs h-8"
                      />
                    </div>
                  )}
                  {!enabledDays[i] && (
                    <span className="text-xs text-muted-foreground italic">Folga</span>
                  )}
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setHoursDialogOpen(false)}>Cancelar</Button>
            <Button
              onClick={handleSaveHours}
              disabled={setHoursMutation.isPending || loadingHours}
              className="bg-primary text-primary-foreground"
            >
              {setHoursMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              Salvar Horários
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
